const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

async function postWhiteboard(path, postData) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(postData),
  });
  const responseData = typeof response.json === 'function'
    ? await response.json().catch(() => null)
    : null;

  if (!response.ok) {
    const error = new Error(responseData?.message || 'Network response was not ok');
    error.status = response.status;
    throw error;
  }

  return responseData;
}

export function fetchHomework(username, date) {
  return postWhiteboard('/whiteboard/get_target_table', {
    user_name: username,
    ...(date ? { date } : {}),
  });
}

export function fetchDeadline(username, deadline) {
  return postWhiteboard('/whiteboard/get_deadline', {
    username,
    deadline,
  });
}

export function fetchUpcomingEvents(username) {
  return postWhiteboard('/whiteboard/upcoming', {
    username,
  });
}

export function fetchClassDashboard(publicToken, date, boardId) {
  return postWhiteboard('/whiteboard/public_class', {
    public_token: publicToken,
    ...(boardId ? { board_id: boardId } : {}),
    ...(date ? { date } : {}),
  });
}

export function fetchSubjectGroups(organizationUid, boardId) {
  return postWhiteboard('/whiteboard/public_subject_groups', {
    organization_uid: organizationUid,
    ...(boardId ? { board_id: boardId } : {}),
  });
}

export async function fetchDeviceSchool(device) {
  const normalizedDevice = String(device || '').trim();
  let accountLookupError;
  try {
    const subjectGroups = await postWhiteboard('/whiteboard/public_subject_groups', {
      device: normalizedDevice,
    });
    if (subjectGroups.organization?.uid) {
      return {
        is_bound: true,
        uid: subjectGroups.organization.uid,
        subjectGroups,
      };
    }
  } catch (error) {
    accountLookupError = error;
    // New links may carry a physical device ID or serial instead of the
    // legacy device-account name. Continue through the binding lookups.
  }

  const response = await fetch(
    `${API_BASE_URL}/qr_code_lock/${encodeURIComponent(normalizedDevice)}/binding/check`
  );

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  const binding = await response.json();
  if (binding.is_bound) return binding;

  // Some installed links use the hardware serial rather than the generated
  // device ID. Fall back to the serial lookup so both formats keep working.
  const serialBinding = await postWhiteboard('/qr_code_lock/binding/check', {
    serial: normalizedDevice,
  });
  if (!serialBinding.is_bound && accountLookupError?.message === 'School link is required.') {
    throw new Error('Classroom device links are not enabled on the homework server yet. Please contact an administrator.');
  }
  return serialBinding;
}

export async function fetchClassDashboards(publicTokens, date, boardId) {
  const results = await Promise.allSettled(
    publicTokens.map((publicToken) => fetchClassDashboard(publicToken, date, boardId))
  );
  const dashboards = results
    .filter((result) => result.status === 'fulfilled')
    .map((result) => result.value);

  if (!dashboards.length && results.length) {
    throw results.find((result) => result.status === 'rejected').reason;
  }

  return {
    dashboards,
    failedCount: results.length - dashboards.length,
  };
}
